import React, { useState, useRef, useEffect } from 'react';

interface Track {
  title: string;
  url: string;
}

type Mood = 'happy' | 'romantic' | 'sad' | 'calm' | 'energetic';

// Using SoundHelix demo tracks (royalty-free). Divided by vibe by song number.
// TODO: replace with curated mood-matched tracks
const MOOD_TRACKS: Record<Mood, Track[]> = {
  happy: [
    { title: '阳光心情', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
    { title: '轻快时光', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3' },
    { title: '晴天', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-11.mp3' },
  ],
  romantic: [
    { title: '心动瞬间', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3' },
    { title: '温柔告白', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-13.mp3' },
    { title: '夜色缠绵', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-15.mp3' },
  ],
  sad: [
    { title: '雨夜独白', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' },
    { title: '远方的你', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-9.mp3' },
  ],
  calm: [
    { title: '静谧午后', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
    { title: '晚风', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3' },
    { title: '入梦', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-14.mp3' },
  ],
  energetic: [
    { title: '心跳加速', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3' },
    { title: '热血沸腾', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3' },
    { title: '突破', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-17.mp3' },
  ],
};

const MOOD_LABEL: Record<Mood, { icon: string; label: string }> = {
  happy: { icon: '🎉', label: '欢快' },
  romantic: { icon: '💕', label: '浪漫' },
  sad: { icon: '💔', label: '抒情' },
  calm: { icon: '🌙', label: '舒缓' },
  energetic: { icon: '🔥', label: '激情' },
};

// Simple keyword fallback if no [MOOD] tag comes through
const KEYWORDS: Array<[Mood, RegExp]> = [
  ['sad',       /难过|伤心|哭|失落|不开心|郁闷|心累|委屈|孤独/],
  ['romantic',  /爱你|喜欢你|想你|亲亲|抱抱|宝贝|心动|甜|浪漫/],
  ['energetic', /加油|冲|兴奋|激动|爽|燃|干就完了|好玩/],
  ['calm',      /晚安|睡觉|累了|休息|平静|安静|放松/],
  ['happy',     /哈哈|嘿嘿|开心|高兴|好玩|笑|有趣|哈哈哈/],
];

export function detectMood(text: string): Mood | null {
  if (!text) return null;
  // 1. explicit [MOOD:xxx] tag
  const tag = text.match(/\[MOOD:(\w+)\]/i);
  if (tag) {
    const m = tag[1].toLowerCase() as Mood;
    if (m in MOOD_TRACKS) return m;
  }
  // 2. keyword fallback
  for (const [mood, re] of KEYWORDS) {
    if (re.test(text)) return mood;
  }
  return null;
}

export default function MusicPlayer() {
  const [mood, setMood] = useState<Mood>('happy');
  const [trackIdx, setTrackIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const tracks = MOOD_TRACKS[mood];
  const track = tracks[trackIdx % tracks.length];
  const moodInfo = MOOD_LABEL[mood];

  // Listen for mood-change events from chat
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { mood?: Mood; autoplay?: boolean };
      if (!detail?.mood || !(detail.mood in MOOD_TRACKS)) return;
      if (detail.mood === mood) return;
      setMood(detail.mood);
      setTrackIdx(0);
      // auto switch song
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = MOOD_TRACKS[detail.mood!][0].url;
          if (isPlaying || detail.autoplay) {
            audioRef.current.play().catch(() => {});
          }
        }
      }, 50);
    };
    window.addEventListener('ai-mood-change', handler);
    return () => window.removeEventListener('ai-mood-change', handler);
  }, [mood, isPlaying]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const ensureAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(track.url);
      audioRef.current.volume = 0.5;
      audioRef.current.addEventListener('play', () => setIsPlaying(true));
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
      audioRef.current.addEventListener('ended', () => nextTrack());
    }
    return audioRef.current;
  };

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const a = ensureAudio();
    if (isPlaying) a.pause();
    else a.play().catch((err) => console.error('Play failed:', err));
  };

  const nextTrack = () => {
    const nextIdx = (trackIdx + 1) % tracks.length;
    setTrackIdx(nextIdx);
    if (audioRef.current) {
      audioRef.current.src = tracks[nextIdx].url;
      audioRef.current.play().catch(() => {});
    }
  };

  const skipNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    nextTrack();
  };

  if (hidden) return null;

  const playerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 80,
    right: 16,
    zIndex: 1000,
    background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)',
    borderRadius: collapsed ? '50%' : 28,
    boxShadow: '0 4px 20px rgba(168, 85, 247, 0.4)',
    display: 'flex',
    alignItems: 'center',
    padding: collapsed ? 0 : '6px 10px 6px 6px',
    width: collapsed ? 48 : 'auto',
    height: collapsed ? 48 : 'auto',
    cursor: collapsed ? 'pointer' : 'default',
    transition: 'all 0.3s ease',
    userSelect: 'none',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: 8,
  };

  const btnStyle: React.CSSProperties = {
    width: 32, height: 32, borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.25)', border: 'none', color: 'white',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, flexShrink: 0,
  };

  const closeBtnStyle: React.CSSProperties = {
    width: 20, height: 20, borderRadius: '50%',
    background: 'rgba(0, 0, 0, 0.2)', border: 'none', color: 'white',
    fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexShrink: 0, padding: 0,
  };

  if (collapsed) {
    return (
      <div style={playerStyle} onClick={() => setCollapsed(false)} title={`${moodInfo.label} · 展开`}>
        <span style={{ fontSize: 22, animation: isPlaying ? 'spin 4s linear infinite' : 'none' }}>{moodInfo.icon}</span>
      </div>
    );
  }

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div style={playerStyle}>
        <button style={btnStyle} onClick={togglePlay} title={isPlaying ? '暂停' : '播放'}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button style={btnStyle} onClick={skipNext} title="下一首">⏭</button>
        <span style={{ fontSize: 18, animation: isPlaying ? 'spin 4s linear infinite' : 'none' }}>{moodInfo.icon}</span>
        <div style={{ color: 'white', fontSize: '0.78rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 600 }}>{track.title}</div>
          <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{moodInfo.label} 模式</div>
        </div>
        <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); setCollapsed(true); }} title="收起">−</button>
        <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); if (audioRef.current) audioRef.current.pause(); setHidden(true); }} title="关闭">×</button>
      </div>
    </>
  );
}
